import api from "./api";

// ============ INTERFACES ============

export interface Product {
  _id: string;
  name: string;
  price: number;
  image?: string;
  images?: string[];
  stock: number;
  category: string;
  active: boolean;
}

export interface GuestCartItem {
  productId: string;
  name: string;
  price: number;
  quantity: number;
  image?: string;
  subtotal: number;
  // Datos locales para UI
  localId?: string;
  customization?: {
    personalizedName?: string;
    notes?: string;
  };
}

export interface UserCartItem {
  product: {
    _id: string;
    title: string;
    name: string;
    price: number;
    image: string;
    images?: string[];
    type?: "físico" | "digital";
    stock: number;
  };
  quantity: number;
  price: number;
  _id: string;
  customization?: {
    personalizedName?: string;
    notes?: string;
  };
}

export interface GuestCart {
  items: GuestCartItem[];
  subtotal: number;
  taxes: number;
  taxRate: number;
  total: number;
  currency: string;
  itemCount: number;
  isGuest: true;
  lastUpdated: number;
}

export interface UserCart {
  _id: string;
  user: string;
  items: UserCartItem[];
  totalItems: number;
  subtotal: number;
  createdAt: string;
  updatedAt: string;
  isGuest: false;
}

export type HybridCart = GuestCart | UserCart;

export interface CartValidationResult {
  cart: {
    items: GuestCartItem[];
    invalidItems: Array<{
      productId: string;
      reason: string;
      name: string;
      availableStock?: number;
    }>;
    subtotal: number;
    taxes: number;
    taxRate: number;
    total: number;
    currency: string;
    itemCount: number;
    isValid: boolean;
  };
  warnings?: string;
}

export interface ProductInfo {
  id: string;
  name: string;
  price: number;
  stock: number;
  image: string | null;
  category: string;
  available: boolean;
}

// ============ STORAGE UTILITIES ============

const GUEST_CART_KEY = "vivi_guest_cart";
const TAX_RATE = 0.15; // 15% ISV en Honduras

class GuestCartStorage {
  static saveCart(cart: GuestCart): void {
    try {
      localStorage.setItem(
        GUEST_CART_KEY,
        JSON.stringify({
          ...cart,
          lastUpdated: Date.now(),
        })
      );
    } catch (error) {
      console.warn("Failed to save guest cart to localStorage:", error);
    }
  }

  static loadCart(): GuestCart {
    if (typeof localStorage === "undefined") {
      return this.createEmptyCart();
    }
    try {
      const stored = localStorage.getItem(GUEST_CART_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        return {
          items: parsed.items || [],
          subtotal: parsed.subtotal || 0,
          taxes: parsed.taxes || 0,
          taxRate: parsed.taxRate || TAX_RATE,
          total: parsed.total || 0,
          currency: parsed.currency || "USD",
          itemCount: parsed.itemCount || 0,
          isGuest: true,
          lastUpdated: parsed.lastUpdated || Date.now(),
        };
      }
    } catch (error) {
      console.warn("Failed to load guest cart from localStorage:", error);
    }

    return this.createEmptyCart();
  }

  static createEmptyCart(): GuestCart {
    return {
      items: [],
      subtotal: 0,
      taxes: 0,
      taxRate: TAX_RATE,
      total: 0,
      currency: "USD",
      itemCount: 0,
      isGuest: true,
      lastUpdated: Date.now(),
    };
  }

  static clearCart(): void {
    try {
      localStorage.removeItem(GUEST_CART_KEY);
    } catch (error) {
      console.warn("Failed to clear guest cart from localStorage:", error);
    }
  }

  static calculateTotals(
    items: GuestCartItem[]
  ): Pick<GuestCart, "subtotal" | "taxes" | "total" | "itemCount"> {
    const subtotal = items.reduce((sum, item) => sum + item.subtotal, 0);
    const taxes = 0; // No taxes applied
    const total = subtotal; // Total equals subtotal
    const itemCount = items.reduce((count, item) => count + item.quantity, 0);

    return { subtotal, taxes, total, itemCount };
  }
}

// ============ HYBRID CART SERVICE ============

class HybridCartService {
  // ============ GUEST CART METHODS ============

  getGuestCart(): GuestCart {
    return GuestCartStorage.loadCart();
  }

  addToGuestCart(
    product: Product,
    quantity: number = 1,
    customization?: { personalizedName?: string; notes?: string }
  ): GuestCart {
    const cart = this.getGuestCart();
    const existingItemIndex = cart.items.findIndex(
      (item) => item.productId === product._id
    );

    if (existingItemIndex >= 0) {
      // Update existing item
      cart.items[existingItemIndex].quantity += quantity;
      cart.items[existingItemIndex].subtotal =
        cart.items[existingItemIndex].price *
        cart.items[existingItemIndex].quantity;
      // Actualizar personalización si se proporciona
      if (customization) {
        cart.items[existingItemIndex].customization = customization;
      }
    } else {
      // Add new item
      const itemImage = product.images?.[0] || product.image;

      console.log('🛒 Adding product to guest cart:', {
        productId: product._id,
        name: product.name,
        hasImage: !!itemImage,
        imagePreview: itemImage ? `${itemImage.substring(0, 50)}...` : 'NO IMAGE'
      });

      const newItem: GuestCartItem = {
        productId: product._id,
        name: product.name,
        price: product.price,
        quantity,
        image: itemImage,
        subtotal: product.price * quantity,
        localId: `local_${Date.now()}_${Math.random()
          .toString(36)
          .substr(2, 9)}`,
      };

      // Agregar personalización si existe
      if (customization) {
        newItem.customization = customization;
      }

      cart.items.push(newItem);
    }

    // Recalculate totals
    const totals = GuestCartStorage.calculateTotals(cart.items);
    const updatedCart = { ...cart, ...totals };

    GuestCartStorage.saveCart(updatedCart);
    return updatedCart;
  }

  updateGuestCartItem(productId: string, quantity: number): GuestCart {
    const cart = this.getGuestCart();
    const itemIndex = cart.items.findIndex(
      (item) => item.productId === productId
    );

    if (itemIndex >= 0) {
      if (quantity <= 0) {
        // Remove item if quantity is 0 or negative
        cart.items.splice(itemIndex, 1);
      } else {
        // Update quantity and subtotal
        cart.items[itemIndex].quantity = quantity;
        cart.items[itemIndex].subtotal = cart.items[itemIndex].price * quantity;
      }

      // Recalculate totals
      const totals = GuestCartStorage.calculateTotals(cart.items);
      const updatedCart = { ...cart, ...totals };

      GuestCartStorage.saveCart(updatedCart);
      return updatedCart;
    }

    return cart;
  }

  removeFromGuestCart(productId: string): GuestCart {
    const cart = this.getGuestCart();
    cart.items = cart.items.filter((item) => item.productId !== productId);

    // Recalculate totals
    const totals = GuestCartStorage.calculateTotals(cart.items);
    const updatedCart = { ...cart, ...totals };

    GuestCartStorage.saveCart(updatedCart);
    return updatedCart;
  }

  clearGuestCart(): GuestCart {
    const emptyCart = GuestCartStorage.createEmptyCart();
    GuestCartStorage.clearCart();
    return emptyCart;
  }

  // ============ USER CART METHODS ============

  async getUserCart(): Promise<UserCart> {
    const response = await api.get<{ data: UserCart }>("/cart");
    return { ...response.data.data, isGuest: false };
  }

  async addToUserCart(
    productId: string,
    quantity: number = 1,
    customization?: { personalizedName?: string; notes?: string },
    previewImage?: string
  ): Promise<UserCart> {
    const response = await api.post<{ data: UserCart }>("/cart/add", {
      productId,
      quantity,
      customization,
      previewImage,
    });
    return { ...response.data.data, isGuest: false };
  }

  async updateUserCartItem(
    productId: string,
    quantity: number
  ): Promise<UserCart> {
    const response = await api.put<{ data: UserCart }>(
      `/cart/item/${productId}`,
      { quantity }
    );
    return { ...response.data.data, isGuest: false };
  }

  async removeFromUserCart(productId: string): Promise<UserCart> {
    const response = await api.delete<{ data: UserCart }>(
      `/cart/item/${productId}`
    );
    return { ...response.data.data, isGuest: false };
  }

  async clearUserCart(): Promise<UserCart> {
    const response = await api.delete<{ data: UserCart }>("/cart/clear");
    return { ...response.data.data, isGuest: false };
  }

  // ============ API UTILITIES FOR GUEST CART ============

  async validateGuestCart(
    items: GuestCartItem[]
  ): Promise<CartValidationResult> {
    const cartItems = items.map((item) => ({
      productId: item.productId,
      quantity: item.quantity,
      name: item.name,
      price: item.price,
    }));

    const response = await api.post<{
      success: boolean;
      data: CartValidationResult["cart"];
    }>("/cart/validate", {
      items: cartItems,
    });

    return {
      cart: response.data.data,
    };
  }

  async getProductsInfo(
    productIds: string[]
  ): Promise<Record<string, ProductInfo>> {
    const response = await api.post<{
      success: boolean;
      data: { products: Record<string, ProductInfo> };
    }>("/cart/products-info", {
      productIds,
    });

    return response.data.data.products;
  }

  async checkProductAvailability(
    productId: string,
    quantity: number = 1
  ): Promise<{
    available: boolean;
    stock: number;
    canFulfill: boolean;
    reason?: string;
  }> {
    const response = await api.get(
      `/cart/product/${productId}/availability?quantity=${quantity}`
    );
    return response.data.data;
  }

  // ============ CART MERGING ============

  async mergeGuestCartWithUser(guestCart: GuestCart): Promise<UserCart> {
    if (guestCart.items.length === 0) {
      return this.getUserCart();
    }

    const guestItems = guestCart.items.map((item) => ({
      productId: item.productId,
      quantity: item.quantity,
    }));

    const response = await api.post<{
      success: boolean;
      data: {
        cart: UserCart;
        mergeResults: {
          added: Array<{ productId: string; quantity: number }>;
          updated: Array<{
            productId: string;
            previousQuantity: number;
            newQuantity: number;
          }>;
          failed: Array<{ productId: string; reason: string }>;
          totalProcessed: number;
        };
      };
    }>("/cart/merge", {
      guestItems,
    });

    // Clear guest cart after successful merge
    this.clearGuestCart();

    return { ...response.data.data.cart, isGuest: false };
  }

  // ============ UTILITY METHODS ============

  hasGuestCartItems(): boolean {
    const cart = this.getGuestCart();
    return cart.items.length > 0;
  }

  getGuestCartItemCount(): number {
    const cart = this.getGuestCart();
    return cart.itemCount;
  }

  getGuestCartTotal(): number {
    const cart = this.getGuestCart();
    return cart.total;
  }

  // Sync guest cart with server (validate items without changing them)
  async syncGuestCart(): Promise<GuestCart> {
    const cart = this.getGuestCart();
    if (cart.items.length === 0) return cart;

    try {
      const validation = await this.validateGuestCart(cart.items);

      // Update cart with server-validated data
      const validItems = validation.cart.items;
      const updatedCart: GuestCart = {
        items: validItems,
        subtotal: validation.cart.subtotal,
        taxes: validation.cart.taxes,
        taxRate: validation.cart.taxRate,
        total: validation.cart.total,
        currency: validation.cart.currency,
        itemCount: validation.cart.itemCount,
        isGuest: true,
        lastUpdated: Date.now(),
      };

      GuestCartStorage.saveCart(updatedCart);
      return updatedCart;
    } catch (error) {
      console.warn("Failed to sync guest cart with server:", error);
      return cart;
    }
  }
}

export default new HybridCartService();
